import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabaseClient } from '../../services/supabase';
import './DeveloperPage.css';
import { ChartLayout, OverviewContent, MethodologyContent } from '../charts/ChartLayout';
import ClimateEquityGapChart from '../charts/ClimateEquityGapChart';
import GeoidPVChart from '../charts/efrancktestchart';
import BayRENMultifamilyTargetingTool from '../../domains/bayren/components/BayRENMultifamilyTargetingTool';
import CaliforniaBayRENTool from '../../domains/bayren/components/CaliforniaBayRENTool';
import PolicyImpactTool from '../tools/PolicyImpactTool';
import PercentDroveAloneWork from '../charts/PercentDroveAloneWork';
import MeansOfTransportation from '../charts/MeansOfTransportation';
import SolarAccelerationChart from '../charts/SolarAccelerationChart';
import { CountiesGHGChart } from '../charts/CountiesGHGChart';
import { AllCitiesGHGChart } from '../charts/AllCitiesGHGChart';
import { PrecalculatedVsLiveComparisonChart } from '../charts/PrecalculatedVsLiveComparisonChart';
import { EquityContent, RelevanceContent } from '../charts/ChartLayout';
import { useLocation } from '../../contexts/LocationContext';

interface Developer {
  id: string;
  name: string;
  email: string;
  role: string;
  full_name?: string;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  category: string;
  priority: string;
  status: string;
  assigned_to?: string;
  assigned_date?: string;
  completed_at?: string;
  completed_by?: string;
}

const DeveloperPage: React.FC = () => {
  const { developerSlug } = useParams<{ developerSlug: string }>();
  const { selectedLocation, selectedGeofips, coreDataLoading } = useLocation();
  const [developer, setDeveloper] = useState<Developer | null>(null);
  const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartActiveView, setChartActiveView] = useState<string>('overview');
  const [hdvSeries, setHdvSeries] = useState<{ year: number; total_ghg: number }[]>([]);
  const [loadingHdv, setLoadingHdv] = useState<boolean>(false);
  const [hdvError, setHdvError] = useState<string | null>(null);
  const [isChris, setIsChris] = useState<boolean>(false);
  const [statewideChartView, setStatewideChartView] = useState<'counties' | 'cities'>('counties');
  const [comparisonChartHandlers, setComparisonChartHandlers] = useState<{
    downloadTotalEmissionsCSV: () => void;
    downloadSectorComparisonCSV: () => void;
    hasSectorComparison: boolean;
  } | null>(null);

  const fetchTasks = async (developerId: string) => {
    try {
      setLoadingTasks(true);
      
      // Fetch assigned tasks (pending and in-progress)
      const { data: assignedData, error: assignedError } = await supabaseClient
        .from('tasks')
        .select('*')
        .eq('assigned_to', developerId)
        .in('status', ['pending', 'in_progress'])
        .order('priority', { ascending: false })
        .order('title');

      if (assignedError) {
        console.error('Error fetching assigned tasks:', assignedError);
        return;
      }

      // Fetch completed tasks
      const { data: completedData, error: completedError } = await supabaseClient
        .from('tasks')
        .select('*')
        .eq('assigned_to', developerId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (completedError) {
        console.error('Error fetching completed tasks:', completedError);
        return;
      }

      setAssignedTasks(assignedData || []);
      setCompletedTasks(completedData || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    const fetchDeveloper = async () => {
      if (!developerSlug) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch all developers from Supabase
        const { data: developers, error } = await supabaseClient
          .from('user_profiles')
          .select('id, email, full_name, role')
          .in('role', ['super_admin', 'developer']);

        if (error) {
          console.error('Error fetching developers:', error);
          setError('Failed to load developer information');
          return;
        }

        // Find developer by slug (email prefix or first name)
        const foundDeveloper = developers?.find(dev => {
          const emailPrefix = dev.email.split('@')[0];
          const firstName = dev.full_name?.split(' ')[0]?.toLowerCase();
          const slug = developerSlug.toLowerCase();
          
          return emailPrefix === slug || firstName === slug;
        });

        if (foundDeveloper) {
          const developerData = {
            id: foundDeveloper.id,
            name: foundDeveloper.full_name || foundDeveloper.email,
            email: foundDeveloper.email,
            role: foundDeveloper.role,
            full_name: foundDeveloper.full_name
          };
          setDeveloper(developerData);
          setIsChris((foundDeveloper.email || '').toLowerCase() === 'cmjones@berkeley.edu');
          
          // Fetch tasks for this developer
          await fetchTasks(foundDeveloper.id);
          
          // If Chris Jones, also fetch the HDV GHG time series
          if ((foundDeveloper.email || '').toLowerCase() === 'cmjones@berkeley.edu') {
            try {
              setLoadingHdv(true);
              setHdvError(null);
              const { data, error: ghgError } = await supabaseClient
                .from('fleet_ghg')
                .select('year,total_ghg,class')
                .eq('class', 'HDV')
                .order('year', { ascending: true });

              if (ghgError) {
                console.error('Error fetching HDV series:', ghgError);
                setHdvError('Failed to load HDV GHG data');
              } else {
                const formatted = (data || [])
                  .filter((row: any) => row.year != null && row.total_ghg != null)
                  .map((row: any) => ({ year: Number(row.year), total_ghg: Number(row.total_ghg) }));
                setHdvSeries(formatted);
              }
            } catch (e) {
              console.error('Exception fetching HDV series:', e);
              setHdvError('Failed to load HDV GHG data');
            } finally {
              setLoadingHdv(false);
            }
          }
        } else {
          setError('Developer not found');
        }
      } catch (err) {
        console.error('Error fetching developer:', err);
        setError('Failed to load developer information');
      } finally {
        setLoading(false);
      }
    };

    fetchDeveloper();
  }, [developerSlug]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#ea580c';
      case 'low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#16a34a';
      case 'in_progress': return '#ea580c';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  /**
   * Get the main chart/tools section title for this developer page.
   * For the Jason Yi page, we intentionally return null so that the
   * only visible title is the one inside the embedded PolicyImpactTool
   * component (avoiding a duplicated \"GHG Policy Impact Tool\" heading).
   */
  const getMainSectionTitle = () => {
    if (isChris) {
      return 'HDV Total GHG (Time Series)';
    }
    if (developerSlug === 'efranck') {
      return 'Multifamily Housing Analysis (PUMS Data)';
    }
    if (developerSlug === 'hinosawa') {
      return 'BayREN Multifamily Energy Efficiency Targeting Tool';
    }
    if (developerSlug === 'martin.lu') {
      return 'Multifamily Energy Efficiency Targeting Tool (California Data)';
    }
    // For Jason Yi, suppress the outer title so the tool's own header
    // is the only \"GHG Policy Impact Tool (Under development)\" text.
    if (developerSlug === 'jasonyi') {
      return null;
    }
    return 'Climate Action Equity Gap Analysis';
  };

  if (loading) {
    return (
      <div className="developer-page">
        <div className="developer-loading">
          <div className="loading-spinner"></div>
          <p>Loading developer page...</p>
        </div>
      </div>
    );
  }

  if (coreDataLoading) {
    return (
      <div className="developer-page">
        <div className="developer-loading">
          <div className="loading-spinner"></div>
          <p>Loading core data for {selectedLocation}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="developer-page">
        <div className="developer-error">
          <h1>Developer Not Found</h1>
          <p>{error}</p>
          <p>The developer "{developerSlug}" could not be found.</p>
        </div>
      </div>
    );
  }

  if (!developer) {
    return (
      <div className="developer-page">
        <div className="developer-error">
          <h1>Developer Not Found</h1>
          <p>The developer "{developerSlug}" could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="developer-page">
      <div className="developer-header">
        <h1>{developer.name}</h1>
        <div className="developer-info">
          <p><strong>Email:</strong> {developer.email}</p>
          <p><strong>Role:</strong> {developer.role.replace('_', ' ').toUpperCase()}</p>
        </div>
      </div>

      <div className="developer-content">
        {/* Developer chart/tool section */}
        <div className="tasks-section">
          {getMainSectionTitle() && (
            <h2>{getMainSectionTitle()}</h2>
          )}
          {isChris ? (
            <ChartLayout
              title="Fleet GHG - Heavy Duty Vehicles (HDV)"
              description="Total GHG for class = HDV over available years from Supabase fleet_ghg."
              chartType="line"
              layout="vertical"
              activeView={chartActiveView}
              onViewChange={setChartActiveView}
              keyMetrics={
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div><strong>Points:</strong> {hdvSeries.length}</div>
                  {hdvSeries.length > 0 && (
                    <div><strong>Latest:</strong> {hdvSeries[hdvSeries.length - 1].total_ghg.toLocaleString()}</div>
                  )}
                </div>
              }
              chartContent={(
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  {loadingHdv ? (
                    <div>Loading HDV series...</div>
                  ) : hdvError ? (
                    <div style={{ color: '#dc2626' }}>{hdvError}</div>
                  ) : hdvSeries.length === 0 ? (
                    <div>No HDV data available.</div>
                  ) : (
                    <svg width={Math.max(560, hdvSeries.length * 60)} height={220} role="img" aria-label="HDV GHG time series">
                      {(() => {
                        const padding = { left: 50, right: 20, top: 20, bottom: 40 };
                        const innerW = Math.max(560, hdvSeries.length * 60) - padding.left - padding.right;
                        const innerH = 220 - padding.top - padding.bottom;
                        const years = hdvSeries.map(p => p.year);
                        const values = hdvSeries.map(p => p.total_ghg);
                        const minY = Math.min(...values);
                        const maxY = Math.max(...values);
                        const yScale = (v: number) => padding.top + innerH - ((v - minY) / (maxY - minY || 1)) * innerH;
                        const xScale = (i: number) => padding.left + (i / (hdvSeries.length - 1 || 1)) * innerW;

                        const points = hdvSeries.map((p, i) => `${xScale(i)},${yScale(p.total_ghg)}`).join(' ');

                        return (
                          <g>
                            {/* Axes */}
                            <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + innerH} stroke="#94a3b8" />
                            <line x1={padding.left} y1={padding.top + innerH} x2={padding.left + innerW} y2={padding.top + innerH} stroke="#94a3b8" />
                            {/* Y labels */}
                            {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => {
                              const v = minY + t * (maxY - minY);
                              const y = yScale(v);
                              return (
                                <g key={idx}>
                                  <line x1={padding.left - 4} y1={y} x2={padding.left} y2={y} stroke="#94a3b8" />
                                  <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#475569">{Math.round(v).toLocaleString()}</text>
                                </g>
                              );
                            })}
                            {/* X labels */}
                            {years.map((yr, i) => (
                              <text key={yr} x={xScale(i)} y={padding.top + innerH + 20} textAnchor="middle" fontSize="12" fill="#475569">{yr}</text>
                            ))}

                            {/* Line */}
                            <polyline fill="none" stroke="#2563eb" strokeWidth={3} points={points} />
                            {/* Points */}
                            {hdvSeries.map((p, i) => (
                              <g key={i}>
                                <circle cx={xScale(i)} cy={yScale(p.total_ghg)} r={4} fill="#1d4ed8" />
                                <title>{`${p.year}: ${p.total_ghg.toLocaleString()}`}</title>
                              </g>
                            ))}
                          </g>
                        );
                      })()}
                    </svg>
                  )}
                </div>
              )}
              overviewContent={
                <OverviewContent
                  title="What this shows"
                  description="Time series of total GHG emissions for Heavy Duty Vehicles (HDV) from the fleet_ghg table."
                />
              }
              methodologyContent={
                <MethodologyContent
                  title="Methodology"
                  description="Data are fetched directly from Supabase (table: fleet_ghg) filtered by class = 'HDV' and ordered by year."
                  dataSources={[ 'Supabase: fleet_ghg' ]}
                  methodology={[ 'Filter class = HDV', 'Order by year ascending', 'Plot total_ghg as a line series' ]}
                />
              }
            />
          ) : developerSlug === 'efranck' ? (
            <GeoidPVChart onMetricsUpdate={() => {}} />
          ) : developerSlug === 'marylaska' ? (
            <>
              <div>Single Family Detached Housing chart has been moved to the Buildings page above the Household Carbon Footprint Map.</div>
              <div style={{ marginTop: '40px' }}>
                <PercentDroveAloneWork 
                  location={selectedLocation}
                  geofips={selectedGeofips}
                  preferredState="California"
                />
              </div>
              <div style={{ marginTop: '40px' }}>
                <MeansOfTransportation 
                  location={selectedLocation}
                  geofips={selectedGeofips}
                />
              </div>
            </>
          ) : developerSlug === 'hinosawa' ? (
            <div>This tool has been moved to the Buildings page.</div>
          ) : developerSlug === 'martin.lu' ? (
            <div>This tool has been moved to the Housing page (People section).</div>
          ) : developerSlug === 'jasonyi' ? (
            <>
              <div style={{ marginTop: '0px' }}>
                <SolarAccelerationChart location={selectedLocation} />
              </div>
            </>
          ) : developerSlug === 'chris' ? (
            <>
              {/* Statewide GHG Inventory Analysis Section */}
              <ChartLayout
                title="Statewide GHG Inventory Analysis"
                description="Analysis of GHG inventories across all California cities and counties from the database."
                chartType="line"
                chartContent={
                  statewideChartView === 'counties' ? (
                    <CountiesGHGChart />
                  ) : (
                    <AllCitiesGHGChart />
                  )
                }
                chartSelector={{
                  options: [
                    { value: 'counties', label: 'Counties' },
                    { value: 'cities', label: 'Cities' }
                  ],
                  value: statewideChartView,
                  onChange: (value) => setStatewideChartView(value as 'counties' | 'cities')
                }}
                overviewContent={
                  <OverviewContent
                    title="Statewide GHG Inventory Overview"
                    description={statewideChartView === 'counties' 
                      ? "This chart displays individual GHG inventory trajectories for each county in the database. Each county is represented by a line showing its total emissions from 2005 to 2023. Click on any line in the chart to highlight a specific county and view its data in the tooltip."
                      : "This chart displays individual GHG inventory trajectories for each city in the database. Each city is represented by a line showing its total emissions from 2005 to 2023. Click on any line in the chart to highlight a specific city and view its data in the tooltip."
                    }
                  >
                    <p>
                      {statewideChartView === 'counties' 
                        ? "The chart displays individual GHG inventory trajectories for each county in the database. Each county is represented by a line showing its total emissions from 2005 to 2023. Click on any line in the chart to highlight a specific county and view its data in the tooltip."
                        : "The chart displays individual GHG inventory trajectories for each city in the database. Each city is represented by a line showing its total emissions from 2005 to 2023. Click on any line in the chart to highlight a specific city and view its data in the tooltip."
                      }
                    </p>
                    <p>
                      These visualizations help identify locations with notable emission trends, compare progress 
                      across different communities, and understand the overall trajectory of California's 
                      local government GHG inventories.
                    </p>
                  </OverviewContent>
                }
                relevanceContent={
                  <RelevanceContent
                    title="Relevance to Climate Action"
                    description="Statewide analysis provides context for individual community efforts and helps identify regional patterns."
                  >
                    <div className="methodology-details-simple">
                      <h5>Benchmarking</h5>
                      <p>Cities can compare their emission trends against statewide averages and other similar communities to gauge progress.</p>
                    </div>
                    <div className="methodology-details-simple">
                      <h5>Regional Patterns</h5>
                      <p>Identifying clusters of cities with similar trends can reveal regional factors affecting emissions, such as economic conditions, infrastructure, or policy environments.</p>
                    </div>
                    <div className="methodology-details-simple">
                      <h5>Best Practices</h5>
                      <p>Cities showing significant emission reductions can serve as models for others, helping identify effective strategies and interventions.</p>
                    </div>
                  </RelevanceContent>
                }
                equityContent={
                  <EquityContent
                    title="Equity Considerations"
                    description="Statewide analysis can reveal equity implications in emissions patterns and climate action."
                  >
                    <div className="methodology-details-simple">
                      <h5>Resource Access</h5>
                      <p>Variations in emission trends may reflect differences in access to funding, technical capacity, or community resources for climate action.</p>
                    </div>
                    <div className="methodology-details-simple">
                      <h5>Community Needs</h5>
                      <p>Understanding how different cities are progressing can help identify communities that may need additional support to achieve emission reduction goals.</p>
                    </div>
                    <div className="methodology-details-simple">
                      <h5>Regional Cooperation</h5>
                      <p>Identifying regional patterns can facilitate cooperative approaches to climate action that benefit multiple communities.</p>
                    </div>
                  </EquityContent>
                }
                methodologyContent={
                  <MethodologyContent
                    title="Methodology"
                    description="Data from the pre-calculated GHG inventory database, aggregated and normalized for statewide analysis."
                    dataSources={[
                      'GHG inventory pre-calculated database (ghg_inventory_precalculated table)',
                      statewideChartView === 'counties' 
                        ? 'All counties with calculated inventories'
                        : 'All cities with calculated inventories',
                      'Years 2005-2023'
                    ]}
                    methodology={[
                      'Data from all locations in the ghg_inventory_precalculated database.',
                      statewideChartView === 'counties'
                        ? 'Each county shown as individual line with total emissions over time (2005-2023).'
                        : 'Each city shown as individual line with total emissions over time (2005-2023).',
                      statewideChartView === 'counties'
                        ? 'Only locations with geography_type = "County" are included in this chart.'
                        : 'Only locations with geography_type = "City" are included in this chart.',
                      'Data excludes locations flagged for data quality issues.',
                      'Each line represents total GHG emissions (MT CO₂e) for one location.',
                      'Limitations: Data may reflect variations in methodology or data quality across different locations.'
                    ]}
                  />
                }
                activeView="overview"
                onViewChange={() => {}}
                showRelevanceEquity={true}
                enableComments={true}
                location={selectedLocation}
              />

              {/* Pre-calculated vs Live-calculated Comparison Section */}
              <ChartLayout
                title="Pre-calculated vs Live-calculated Comparison"
                description="Comparison between pre-calculated GHG inventory data (from database) and live-calculated data (from application chart) for the selected location. This helps identify discrepancies and validate calculation accuracy."
                chartType="line"
                chartContent={
                  <PrecalculatedVsLiveComparisonChart 
                    location={selectedLocation}
                    onDownloadHandlersReady={setComparisonChartHandlers}
                  />
                }
                onDownloadCSV={comparisonChartHandlers?.downloadTotalEmissionsCSV}
                csvFilename={selectedLocation ? `${selectedLocation}_Precalculated_vs_Live_Total_Emissions` : 'Precalculated_vs_Live_Total_Emissions'}
                overviewContent={
                  <OverviewContent
                    title="Comparison Overview"
                    description="This chart compares pre-calculated GHG inventory data stored in the database with live-calculated data from the sector-based GHG inventory chart. Differences indicate potential issues with data sources, calculation methods, or data updates."
                  >
                    <p>
                      The pre-calculated data comes from the <code>ghg_inventory_precalculated</code> table, which stores 
                      calculated emissions for all locations using the same methodology as the live chart. The live-calculated 
                      data is computed on-the-fly by the application using current data sources and includes any user modifications 
                      from the Waste GHGs chart.
                    </p>
                    <p>
                      Differences between the two sources may indicate:
                    </p>
                    <ul>
                      <li>Data source updates (e.g., new waste data, updated VMT values)</li>
                      <li>User modifications to waste data in the Waste GHGs chart</li>
                      <li>Calculation methodology changes</li>
                      <li>Data quality issues or missing data points</li>
                    </ul>
                    <p>
                      <strong>Note:</strong> Check the browser console for detailed year-by-year comparison tables showing 
                      exact values and differences for each location.
                    </p>
                  </OverviewContent>
                }
                methodologyContent={
                  <MethodologyContent
                    title="Methodology"
                    description="Comparison methodology for validating pre-calculated vs live-calculated GHG inventory data."
                    dataSources={[
                      'Pre-calculated: ghg_inventory_precalculated table (total_emissions by year)',
                      'Live-calculated: Sum of all sector datasets from Sector-Based GHG Inventory chart',
                      'Location: Selected location only'
                    ]}
                    methodology={[
                      'Fetch pre-calculated total_emissions by year from ghg_inventory_precalculated table for each location',
                      'Fetch live-calculated data by calling fetchGHGInventoryData() and summing all sector datasets',
                      'Calculate differences and percent differences for each year',
                      'Display both series as line charts with detailed comparison table',
                      'Log detailed comparison to browser console for debugging'
                    ]}
                  />
                }
                activeView="overview"
                onViewChange={() => {}}
                showRelevanceEquity={false}
                enableComments={false}
                location={selectedLocation}
              />
              
              {/* Additional CSV Download Button for Sector Comparison */}
              {comparisonChartHandlers?.hasSectorComparison && (
                <div style={{ 
                  maxWidth: '1200px', 
                  margin: '10px auto 20px auto', 
                  padding: '0 25px',
                  display: 'flex', 
                  gap: '10px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={comparisonChartHandlers.downloadSectorComparisonCSV}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                    title="Download Sector Comparison CSV"
                  >
                    📊 Download Sector Comparison CSV
                  </button>
                </div>
              )}
            </>
          ) : (
            <ClimateEquityGapChart />
          )}
        </div>

        {/* Assigned Tasks Section */}
        <div className="tasks-section">
          <h2>Current Tasks</h2>
          {loadingTasks ? (
            <div className="tasks-loading">
              <div className="loading-spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : assignedTasks.length > 0 ? (
            <div className="tasks-list">
              {assignedTasks.map(task => (
                <div key={task.id} className="task-card assigned">
                  <div className="task-header">
                    <h3 className="task-title">{task.title}</h3>
                    <div className="task-badges">
                      <span 
                        className="priority-badge" 
                        style={{ backgroundColor: getPriorityColor(task.priority) }}
                      >
                        {task.priority.toUpperCase()}
                      </span>
                      <span 
                        className="status-badge" 
                        style={{ backgroundColor: getStatusColor(task.status) }}
                      >
                        {task.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  {task.description && (
                    <p className="task-description">{task.description}</p>
                  )}
                  <div className="task-meta">
                    <span className="task-category">{task.category}</span>
                    {task.assigned_date && (
                      <span className="task-date">Assigned: {task.assigned_date}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-tasks">
              <p>No current tasks assigned</p>
            </div>
          )}
        </div>

        {/* Completed Tasks Section */}
        <div className="tasks-section">
          <h2>Completed Tasks</h2>
          {loadingTasks ? (
            <div className="tasks-loading">
              <div className="loading-spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : completedTasks.length > 0 ? (
            <div className="tasks-list">
              {completedTasks.map(task => (
                <div key={task.id} className="task-card completed">
                  <div className="task-header">
                    <h3 className="task-title">{task.title}</h3>
                    <div className="task-badges">
                      <span 
                        className="priority-badge" 
                        style={{ backgroundColor: getPriorityColor(task.priority) }}
                      >
                        {task.priority.toUpperCase()}
                      </span>
                      <span 
                        className="status-badge completed" 
                        style={{ backgroundColor: getStatusColor(task.status) }}
                      >
                        ✅ COMPLETED
                      </span>
                    </div>
                  </div>
                  {task.description && (
                    <p className="task-description">{task.description}</p>
                  )}
                  <div className="task-meta">
                    <span className="task-category">{task.category}</span>
                    {task.assigned_date && (
                      <span className="task-date">Assigned: {task.assigned_date}</span>
                    )}
                    {task.completed_at && (
                      <span className="task-date">Completed: {task.completed_at.split('T')[0]}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-tasks">
              <p>No completed tasks yet</p>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="developer-stats">
          <h3>Task Summary</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">{assignedTasks.length}</span>
              <span className="stat-label">Current Tasks</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{completedTasks.length}</span>
              <span className="stat-label">Completed Tasks</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{assignedTasks.length + completedTasks.length}</span>
              <span className="stat-label">Total Tasks</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperPage;
